// src/pages/ApplyNow.js
import React, { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import emailjs from '@emailjs/browser';
import Navbar from "../components/layout/Navbar";

export default function ApplyNow() {
  const formRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});

  // ✅ GET COLLEGE NAME FROM URL PARAMETER
  const collegeName = searchParams.get('college') || 'General - Apply Page';

  // STRICT VALIDATION
  const validateForm = () => {
    const newErrors = {};
    const formData = new FormData(formRef.current);
    
    // Name - letters only
    const name = formData.get('name')?.trim();
    if (!name || name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(name)) {
      newErrors.name = 'Name can only contain letters';
    }

    // Email validation
    const email = formData.get('email')?.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone - exactly 10 digits
    if (phone.length !== 10) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }

    // City - letters only
    const city = formData.get('city')?.trim();
    if (!city || city.length < 2) {
      newErrors.city = 'City must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(city)) {
      newErrors.city = 'City can only contain letters';
    }

    // Course
    const course = formData.get('course');
    if (!course || course === '') {
      newErrors.course = 'Please select a course';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // ✅ PREPARE EMAIL DATA WITH COLLEGE NAME
    const formData = new FormData(formRef.current);
    const templateParams = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      city: formData.get('city'),
      course: formData.get('course'),
      qualification: formData.get('qualification') || 'Not specified',
      marks: formData.get('marks') || 'Not specified',
      message: formData.get('message') || 'No message',
      // ✅ TRACKING FIELDS WITH COLLEGE NAME
      college_name: collegeName,
      form_source: collegeName === 'General - Apply Page' ? 'apply_page' : 'homepage_card_redirect',
      page_url: window.location.href,
      submission_time: new Date().toLocaleString('en-IN', { 
        timeZone: 'Asia/Kolkata',
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    };

    // Send via EmailJS
    emailjs
      .send('service_u0g0bqj', 'template_naso7ph', templateParams, '0JNndpSn5KZm_5yLJ')
      .then(() => {
        setLoading(false);
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((error) => {
        setLoading(false);
        console.error('EmailJS error:', error);
        alert('Failed to submit. Please check your internet and try again.');
      });
  };

  return (
    <>
      <Navbar />

      {/* INTERNAL CSS */}
      <style>
        {`
          * { box-sizing: border-box; }
          .apply-form-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
          }
          .full-width { grid-column: span 2; }

          @media (max-width: 768px) {
            .apply-page-content {
              padding-top: 100px !important;
            }
            .apply-form-grid {
              grid-template-columns: 1fr !important;
            }
            .full-width { grid-column: span 1 !important; }
          }

          .custom-input:focus, .custom-select:focus, .custom-textarea:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15) !important;
            background-color: #ffffff !important;
          }

          .input-error {
            border-color: #ef4444 !important;
            background-color: #fee2e2 !important;
          }

          .error-text {
            color: #ffffff;
            background-color: rgba(220, 38, 38, 0.9);
            padding: 4px 8px;
            border-radius: 6px;
            font-size: 0.875rem;
            margin-top: 0.5rem;
            font-weight: 600;
          }
        `}
      </style>

      <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
        {/* Blurred Background */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url("/images/applynowbg.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(8px)',
            transform: 'scale(1.1)',
            zIndex: 0
          }} 
        />

        {/* Content */}
        <div 
          className="apply-page-content"
          style={{
            position: 'relative',
            zIndex: 1,
            minHeight: '100vh',
            padding: '120px 20px 60px',
            fontFamily: "'Montserrat', sans-serif",
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start'
          }}
        >
          <div
            className="apply-card"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              width: '100%',
              maxWidth: '850px',
              borderRadius: '20px',
              padding: '40px',
              backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url("/images/applynowbg.png")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
          >
            
            {!submitted ? (
              <>
                {/* Header - ✅ SHOWS COLLEGE NAME */}
                <div style={{ textAlign: 'center', marginBottom: '35px' }}>
                  <h1 style={{
                    color: '#ffffff',
                    fontSize: '2.5rem',
                    fontWeight: '800',
                    marginBottom: '10px',
                    lineHeight: '1.2'
                  }}>
                    Apply Now
                  </h1>
                  {/* ✅ SHOW COLLEGE NAME IF PRESENT */}
                  {collegeName !== 'General - Apply Page' && (
                    <div style={{
                      color: '#60a5fa',
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      marginTop: '10px',
                      marginBottom: '10px'
                    }}>
                      {collegeName}
                    </div>
                  )}
                  <p style={{ color: '#d7e7ff', fontSize: '1rem', fontWeight: '500' }}>
                    Fill in your details and we'll get back to you shortly
                  </p>
                </div>

                {/* Form */}
                <form ref={formRef} onSubmit={handleSubmit} className="apply-form-grid">
                  
                  {/* Full Name */}
                  <div className="full-width" style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={labelStyle}>Full Name *</label>
                    <input 
                      name="name"
                      type="text" 
                      placeholder="Your full name" 
                      required 
                      className={`custom-input ${errors.name ? 'input-error' : ''}`}
                      style={inputStyle}
                    />
                    {errors.name && <p className="error-text">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={labelStyle}>Email Address *</label>
                    <input 
                      name="email"
                      type="email" 
                      placeholder="Your email address" 
                      required 
                      className={`custom-input ${errors.email ? 'input-error' : ''}`}
                      style={inputStyle}
                    />
                    {errors.email && <p className="error-text">{errors.email}</p>}
                  </div>

                  {/* Mobile */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={labelStyle}>Mobile Number *</label>
                    <input 
                      name="phone"
                      type="tel" 
                      value={phone}
                      maxLength="10"
                      placeholder="Your mobile number" 
                      required 
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPhone(digits);
                        setErrors({ ...errors, phone: '' });
                      }}
                      className={`custom-input ${errors.phone ? 'input-error' : ''}`}
                      style={inputStyle}
                    />
                    {errors.phone && <p className="error-text">{errors.phone}</p>}
                  </div>

                  {/* City */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={labelStyle}>City *</label>
                    <input 
                      name="city"
                      type="text" 
                      placeholder="Your City" 
                      required 
                      className={`custom-input ${errors.city ? 'input-error' : ''}`}
                      style={inputStyle}
                    />
                    {errors.city && <p className="error-text">{errors.city}</p>}
                  </div>

                  {/* Course */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={labelStyle}>Interested Course *</label>
                    <select 
                      name="course"
                      required 
                      defaultValue="" 
                      className={`custom-select ${errors.course ? 'input-error' : ''}`}
                      style={inputStyle}
                    >
                      <option value="" disabled>- Select Course -</option>
                      <option value="btech-cse">B.Tech CSE</option>
                      <option value="bba">BBA</option>
                      <option value="bca">BCA</option>
                      <option value="mba">MBA</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.course && <p className="error-text">{errors.course}</p>}
                  </div>

                  {/* Qualification */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={labelStyle}>Current Qualification</label>
                    <input
                      name="qualification"
                      type="text"
                      placeholder="e.g., 12th Pass, Graduate"
                      className="custom-input"
                      style={inputStyle}
                    />
                  </div>

                                   {/* Percentage */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={labelStyle}>Approx. Percentage / CGPA</label>
                    <input 
                      name="marks"
                      type="text" 
                      placeholder="e.g., 85% or 8.5" 
                      className="custom-input"
                      style={inputStyle}
                    />
                  </div>

                  {/* Message */}
                  <div className="full-width" style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={labelStyle}>Message / Query (Optional)</label>
                    <textarea 
                      name="message"
                      rows="4" 
                      placeholder="Tell us about your academic goals..."
                      className="custom-textarea"
                      style={{ ...inputStyle, resize: 'vertical' }}
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <div className="full-width" style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button 
                      type="submit" 
                      disabled={loading}
                      style={{
                        background: loading ? '#94a3b8' : 'linear-gradient(to right, #0ea5e9, #2563eb)',
                        color: 'white',
                        fontWeight: '700',
                        padding: '16px 40px',
                        borderRadius: '50px',
                        border: 'none',
                        fontSize: '1.1rem',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        width: '100%',
                        maxWidth: '400px',
                        boxShadow: '0 10px 25px -5px rgba(37, 99, 235, 0.4)',
                        opacity: loading ? 0.7 : 1,
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        if (!loading) e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {loading ? (
                        <>
                          <span style={{ marginRight: '8px' }}>⏳</span>
                          Submitting...
                        </>
                      ) : (
                        'Submit Application →'
                      )}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              // SUCCESS MESSAGE
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: '#dcfce7', 
                  borderRadius: '50%', 
                  margin: '0 auto 20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center'
                }}>
                  <span style={{ fontSize: "2.5rem", color: "#16a34a" }}>✓</span>
                </div>
                <h2 style={{ 
                  color: "#ffffff", 
                  fontSize: "2rem", 
                  fontWeight: "bold", 
                  marginBottom: '10px' 
                }}>
                  Application Submitted!
                </h2>
                <p style={{ 
                  color: "#e5e7eb", 
                  fontSize: '1.1rem', 
                  marginBottom: '30px' 
                }}>
                  We have received your details and will contact you shortly.
                </p>
                <button 
                  onClick={() => window.close()}
                  style={{
                    background: "#ffffff", 
                    color: "#2563eb", 
                    padding: '12px 30px',
                    borderRadius: '10px',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginRight: '10px'
                  }}
                >
                  Close Tab
                </button>
                <button 
                  onClick={() => window.location.href = '/'}
                  style={{
                    background: "#2563eb", 
                    color: "#ffffff", 
                    padding: '12px 30px',
                    borderRadius: '10px',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Go to Home
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Shared styles
const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  color: '#ffffff',
  fontWeight: '600',
  fontSize: '0.9rem',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
};

const inputStyle = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: '10px',
  border: '2px solid #e2e8f0',
  backgroundColor: '#f8fafc',
  fontSize: '1rem',
  color: '#1e293b',
  outline: 'none',
  transition: 'all 0.2s ease'
};

